import type { ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import type { GarmentCard } from '@fitting-room/contracts'
import { bestImage } from '../lib/media'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * A gown tapped in the grid, as large as the screen allows, over the grid.
 *
 * The owner's words (16 October): a photo in the grid "opens big, and you can
 * close it" — not a change to one at a time. So this is a dialog over the
 * grid rather than a trip into the feed, and the grid under it never moves:
 * closing it is the grid exactly where she left it, with focus back on the
 * photo she tapped.
 *
 * The name and the same two marks sit under the photo, so a gown she opened
 * to look at properly can be chosen without closing it first; they are the
 * step's own buttons (`marks`), so a press here is the same press as on the
 * card. The photo is the feed's derivative (`bestImage`), uncropped — the card
 * cuts a standing gown to 2:3, and seeing the whole of it is why she tapped.
 *
 * Kept inside the room's wrapper rather than portalled to `document.body`,
 * for the reason the feed's own lightbox gives: the tenant's `--fr-*` tokens
 * are written onto that wrapper, and a portal would leave the dialog in the
 * fallback palette.
 */
export function GownLightbox({
  card,
  locale,
  marks,
  count,
  onClose,
}: {
  readonly card: GarmentCard
  readonly locale: string
  /** The pick marks for this gown; `describedBy` is the id of its name. */
  readonly marks: (describedBy: string) => ReactNode
  /** The count a press here just changed, drawn over the foot of the photo. */
  readonly count?: ReactNode
  readonly onClose: () => void
}) {
  const t = uiStrings(locale)
  const nameId = useId()
  const name = text(card.garment.name, locale)
  const photo = bestImage(card.garment.media)
  const [broken, setBroken] = useState(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const latestClose = useRef(onClose)
  latestClose.current = onClose

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  /*
   * Escape, and a focus trap, on the document rather than on the dialog: a
   * mark is disabled while its press is in flight, a disabled button drops
   * focus to the body, and a key handler on the dialog would then hear
   * nothing until she clicked back into it.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        latestClose.current()
        return
      }
      if (event.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const stops = [...dialog.querySelectorAll<HTMLElement>('button:not([disabled])')]
      const first = stops[0]
      const last = stops[stops.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      const inside = active instanceof Node && dialog.contains(active)
      if (event.shiftKey ? !inside || active === first : !inside || active === last) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div
      ref={dialogRef}
      className="fr-lightbox fr-lightbox-gown"
      role="dialog"
      aria-modal="true"
      aria-labelledby={nameId}
      /*
       * A tap anywhere but the photo, the name and the buttons closes it. A
       * click, not a pointerdown: closing on the press would unmount the
       * dialog before the release, and the click that follows would land on
       * whichever grid photo was under her thumb and open that one.
       */
      onClick={(event) => {
        if (event.target instanceof Element && !event.target.closest('img, button, .fr-lightbox-bar')) onClose()
      }}
    >
      <div className="fr-lightbox-frame">
        {photo && !broken ? (
          <img src={photo.src} alt={photo.alt || name} onError={() => setBroken(true)} />
        ) : (
          <span className="fr-eyebrow">{name}</span>
        )}
        {count}
      </div>
      <div className="fr-lightbox-bar">
        <p id={nameId} className="fr-lightbox-name">
          {name}
        </p>
        <div className="fr-grid-marks">{marks(nameId)}</div>
      </div>
      <button
        ref={closeRef}
        type="button"
        className="fr-iconbtn fr-lightbox-close"
        aria-label={t.closePhoto}
        onClick={onClose}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}
